import { PropsWithChildren, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthState } from '#features/auth/state';
import { withSubscribe } from '#lib/withSubscribe';
import { isLoggedIn, isUnauthenticated } from '#services/auth/model';

export type WithFallbacks = {
  /**
   * The route to navigate to when the user can login.
   */
  alternativeRoute: string;
  /**
   * An optional ReactNode to render while logging in and logging out
   */
  fallback?: ReactNode;
};

type ProtectedElementProps = PropsWithChildren<WithFallbacks>;

/**
 * A component that marks its children as "protected" and requires authentication
 * in order to render them. If the user is not authenticated this component
 * navigates to a fallback route, for example to prompt the user for authentication
 * or blocks the user interface.
 */
const ProtectedElementComponent = (props: ProtectedElementProps) => {
  const authState = useAuthState();
  const location = useLocation();

  if (isLoggedIn(authState)) {
    return <>{props.children}</>;
  }

  if (isUnauthenticated(authState)) {
    return <Navigate to={props.alternativeRoute} state={{ from: location }} />;
  }

  return <>{props.fallback}</>;
};

export const ProtectedElement = withSubscribe(ProtectedElementComponent);
